import { faUserTag } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  RegularDropdownComponent, type RegularDropdownProps,
} from '~/_shared/baseComponents/dropdown';
import { type InputSize } from '~/_shared/baseComponents/inputs';
import type { ColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { useGetMapColumns } from './useGetMapColumns';

type MapColumnPickerDropdownContainerProps = Readonly<{
  mapId: number;
  virtualSpreadsheetId: number;
  onChange: (columnId: ColumnId) => void;
  triggerHeight?: InputSize;
  triggerStyle?: RegularDropdownProps<ColumnId>['triggerStyle'];
  placeholder?: string;
}>;

export const MapColumnPickerDropdownContainer: FC<MapColumnPickerDropdownContainerProps> = (props) => {
  const { mapId, virtualSpreadsheetId } = props;
  const { columns, isLoading, error, getMapColumns } = useGetMapColumns();
  const [value, setValue] = useState<ColumnId | null>(null);

  useEffect(() => {
    if (!mapId || !virtualSpreadsheetId) {
      return;
    }

    getMapColumns(mapId, virtualSpreadsheetId);
  }, [getMapColumns, mapId, virtualSpreadsheetId]);

  const dropdownOptions = useMemo(() => columns.map(({ columnId, name }) => ({ value: columnId, name })), [columns]);

  useEffect(() => {
    if (!dropdownOptions.find(o => o.value === value)) {
      setValue(null);
    }
  }, [dropdownOptions, value]);

  const onChange = useCallback((columnId: ColumnId) => {
    setValue(columnId);
    const propagate = props.onChange;
    propagate(columnId);
  }, [props.onChange]);

  return (
    <RegularDropdownComponent
      options={dropdownOptions}
      value={value}
      onChange={onChange}
      isLoading={isLoading || !!error}
      prefixTriggerIcon={faUserTag}
      triggerStyle={props.triggerStyle}
      placeholder={props.placeholder}
      inputSize={props.triggerHeight}
    />
  );
};
